import { useRef, useState } from "react";
import { TBusinessTypes } from "../../../../lib/types/business_types";
import { useBusinessTypes } from "../../../../services/backoffice/business-types-service";
import { Spinner } from "../../../common/spinner";

export const BusinessTypesAdmin = () => {
    const { businessTypes, getAllBusinessTypes, workingState, updateBusinessType, addBusinessType } = useBusinessTypes();
    const [searchName, setSearchName] = useState<string>("");
    const [isSearchingBT, setIsSearchingBT] = useState<boolean>(false);
    const [editingBTId, setEditingBTId] = useState<number | undefined>(undefined);
    const inputBTNameRef = useRef<HTMLInputElement>(null);
    const [addingNewBT, setAddingNewBT] = useState(false);
    const [bTExists, setBTExists] = useState(false);
    const [bTAddSuccess, setBTAddSuccess] = useState(false);

    const applyFiltersBTSearchHandler = () => {
        searchBTByNameHandler(searchName);
    };
    const searchBTByNameHandler = async (nameToSearch: string) => {
        if (isSearchingBT) return;

        setIsSearchingBT(true);
        await getAllBusinessTypes({
            name: nameToSearch.trim(),
        });

        setIsSearchingBT(false);
    };
    const resetFiltersHandler = () => {
        if (inputBTNameRef.current !== null) {
            inputBTNameRef.current.value = "";
        }
        setEditingBTId(undefined);
        setSearchName("");
        searchBTByNameHandler("");
        setBTAddSuccess(false);
        setBTExists(false);
    };

    const handleUpdateBusinessType = async (updatedName: string, id: number) => {
        await updateBusinessType({
            name: updatedName,
            id: id,
        });
    };

    let btAddSuccessTimeout: ReturnType<typeof setTimeout> | undefined;

    const addNewBTButtonHandler = async () => {
        setBTAddSuccess(false);
        setBTExists(false);
        const newBTName = inputBTNameRef.current?.value;

        if (!newBTName) {
            return;
        }
        const bTAlreadyExists = businessTypes.some((bt) => bt.name.toLowerCase() === newBTName.trim().toLowerCase());

        if (bTAlreadyExists) {
            setBTExists(true);
            setSearchName(newBTName);
            searchBTByNameHandler(newBTName);
            setBTAddSuccess(false);
            return;
        }
        setBTExists(false);
        setAddingNewBT(true);
        await addBusinessType(newBTName);
        setBTAddSuccess(true);
        inputBTNameRef.current.value = "";
        setAddingNewBT(false);
        if (btAddSuccessTimeout) {
            clearTimeout(btAddSuccessTimeout);
        }

        btAddSuccessTimeout = setTimeout(() => {
            setBTAddSuccess(false);
        }, 6000);
    };

    return (
        <div className="admin-section p-3 mb-2">
            <div className="row">
                <div className="col-lg-5">
                    <h4 className="text-secondary fw-bold ">Business Types</h4>
                </div>
                <div className="col">
                    <div className="input-group mb-3">
                        <button className="btn btn-light text-primary border-primary rounded-3 me-2" onClick={resetFiltersHandler}>
                            {" "}
                            <i className="fa-solid fa-arrows-rotate"></i>
                        </button>
                        <input
                            type="text"
                            className="form-control rounded-end-0 rounded-start-3"
                            placeholder="Search Business Type"
                            aria-label="Search Business Type"
                            aria-describedby="button-Search-Business-Type"
                            value={searchName}
                            onChange={(e) => setSearchName(e.target.value)}
                            disabled={editingBTId !== undefined}
                        />
                        <button
                            className="btn btn-light border rounded-start-0 rounded-end-3"
                            type="button"
                            id="button-Search-Business-Type"
                            onClick={applyFiltersBTSearchHandler}
                            disabled={editingBTId !== undefined}
                        >
                            <i className="fa-solid fa-magnifying-glass me-2"></i>
                            Search
                        </button>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col"></div>
                <div className="col-lg-7">
                    <div className="input-group mb-2">
                        <input
                            type="text"
                            className="form-control rounded-end-0 rounded-start-3"
                            placeholder="Add New Business Type"
                            aria-label="Add Business Type"
                            aria-describedby="button-Add-Business-Type"
                            disabled={editingBTId !== undefined}
                            ref={inputBTNameRef}
                        />
                        <button
                            className="btn btn-light border text-primary border-primary rounded-start-0 rounded-end-3"
                            type="button"
                            id="button-Search-Business-Type"
                            onClick={addNewBTButtonHandler}
                            disabled={editingBTId !== undefined}
                        >
                            {!addingNewBT && (
                                <>
                                    <i className="fa-solid fa-circle-plus me-2"></i>
                                    <span>Add New Business Type</span>
                                </>
                            )}
                            {addingNewBT && <Spinner spinnerColor="text-primary" />}
                        </button>
                    </div>
                    {bTExists && (
                        <div className="alert alert-danger text-danger py-2 px-3 rounded-3 mb-2" role="alert">
                            <i className="fa-solid fa-circle-exclamation me-2"></i>
                            <small>{inputBTNameRef.current?.value} already exists.</small>
                        </div>
                    )}
                    {bTAddSuccess && (
                        <div className="alert alert-success py-2 px-3 rounded-3 mb-2" role="alert">
                            <i className="fa-solid fa-circle-check me-2"></i>
                            <small>New Business Type Added Successfully!</small>
                        </div>
                    )}
                </div>
            </div>
            {workingState === "working" && (
                <p className="text-primary">
                    <Spinner spinnerColor="text-primary" />
                </p>
            )}
            {workingState === "done" && (
                <>
                    <BusinessTypesTable
                        businessTypes={businessTypes}
                        editingBTId={editingBTId}
                        setEditingBTId={setEditingBTId}
                        updateBusinessType={handleUpdateBusinessType}
                    />
                </>
            )}
        </div>
    );
};

export const BusinessTypesTable = ({
    businessTypes,
    editingBTId,
    setEditingBTId,
    updateBusinessType,
}: {
    businessTypes: TBusinessTypes[];
    editingBTId: number | undefined;
    setEditingBTId: (id: number | undefined) => void;
    updateBusinessType: (name: string, id: number) => void;
}) => {
    return (
        <div className="table-responsive rounded-3 employeeRolesTable mb-2">
            <table className="table table-striped-columns table-hover table-bordered m-0">
                <thead className="table-dark align-middle sticky-top">
                    <tr>
                        <th scope="col"></th>
                        <th scope="col">Name</th>
                        <th scope="col">Industry</th>
                        <th scope="col"></th>
                    </tr>
                </thead>
                <tbody>
                    {businessTypes.map((btype, index) => (
                        <BusinessTypeRow
                            key={index}
                            businessType={btype}
                            index={index + 1}
                            editingBTId={editingBTId}
                            setEditingBTId={setEditingBTId}
                            updateBusinessType={updateBusinessType}
                        />
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export const BusinessTypeRow = ({
    businessType,
    index,
    editingBTId,
    setEditingBTId,
    updateBusinessType,
}: {
    businessType: TBusinessTypes;
    index: number;
    editingBTId: number | undefined;
    setEditingBTId: (id: number | undefined) => void;
    updateBusinessType: (name: string, id: number) => void;
}) => {
    const isEditing = editingBTId === businessType.id;
    const btEditNameRef = useRef<HTMLInputElement>(null);
    const [emptyNameError, setEmptyNameError] = useState(false);
    const [savingNewBTName, setSavingNewBTName] = useState(false);

    const editBTHandler = (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setEditingBTId(businessType.id);
    };

    const saveBTHandler = async () => {
        const inputName = btEditNameRef.current?.value?.trim() || "";

        if (inputName.length === 0) {
            setEmptyNameError(true);
            return;
        }
        setSavingNewBTName(true);
        await updateBusinessType(inputName, businessType.id);
        setSavingNewBTName(false);
        setEmptyNameError(false);
        setEditingBTId(undefined);
    };

    return (
        <>
            <tr className="table-light align-middle">
                <th scope="row">{index}</th>
                {!isEditing && (
                    <>
                        <td className="fw-bold" style={{ width: "50%" }}>
                            {businessType.name}
                        </td>
                        <td>Dental</td>
                        <td className="text-center">
                            <button className="btn btn-sm btn-light rounded-3" onClick={(e: any) => editBTHandler(e)}>
                                <i className="fa-regular fa-pen-to-square fa-xs"></i>
                            </button>
                        </td>
                    </>
                )}
                {isEditing && (
                    <>
                        <td style={{ width: "50%" }}>
                            <form className="row row-cols-auto g-2">
                                <div className="col">
                                    <input
                                        type="text"
                                        ref={btEditNameRef}
                                        className={`form-control form-control-sm rounded-3 ${emptyNameError ? "is-invalid" : ""}`}
                                        defaultValue={businessType.name}
                                        aria-label={businessType.name}
                                        maxLength={25}
                                    />
                                </div>
                                <div className="col">
                                    <button type="button" className="btn btn-primary btn-sm me-2" onClick={saveBTHandler}>
                                        {!savingNewBTName && <span>Save</span>}
                                        {savingNewBTName && <Spinner />}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-danger btn-sm"
                                        onClick={() => {
                                            setEmptyNameError(false);
                                            setEditingBTId(undefined);
                                        }}
                                        disabled={savingNewBTName}
                                    >
                                        Cancel
                                    </button>
                                </div>
                                <div className="col"></div>
                            </form>
                        </td>

                        <td>Dental</td>
                        <td className="text-center">
                            <button className="btn btn-sm btn-light rounded-3" disabled>
                                <i className="fa-regular fa-pen-to-square fa-xs"></i>
                            </button>
                        </td>
                    </>
                )}
            </tr>
        </>
    );
};
